import { useState } from "react"
import { Badge } from "@/components/ui/Badge"
import { Button } from "@/components/ui/Button"
import { Input } from "@/components/ui/Input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/Select"
import { type Product, type Status, PRODUCTS, SUPPLIERS, units } from "../data"

const ORDER: Status[] = ["buy", "little_history", "no_plan", "enough"]
const ALL = "all"
const NONE = "none"

function Suggestion({ product }: { product: Product }) {
  if (product.status === "buy") {
    return (
      <div className="text-right">
        <div className="text-xs">We suggest</div>
        <div className="text-xl font-semibold tabular-nums">{units(product.suggested!)}</div>
      </div>
    )
  }
  if (product.status === "enough") return <Badge variant="success">Enough stock</Badge>
  if (product.status === "little_history") return <Badge variant="warning">Too little sales history</Badge>
  return <Badge variant="secondary">No plan, buyer decides</Badge>
}

interface Props {
  addedIds: string[]
  onAdd: (product: Product) => void
}

// Every product not in the plan yet, the ones that need buying first, each
// with the same reasoning the Needs Buying page shows.
export default function AddProducts({ addedIds, onAdd }: Props) {
  const [search, setSearch] = useState("")
  const [supplier, setSupplier] = useState(ALL)

  const rows = PRODUCTS
    .filter((p) => !addedIds.includes(p.id))
    .filter((p) => `${p.name} ${p.sku}`.toLowerCase().includes(search.trim().toLowerCase()))
    .filter((p) => supplier === ALL || (supplier === NONE ? p.supplier === null : p.supplier === supplier))
    .sort((a, b) => ORDER.indexOf(a.status) - ORDER.indexOf(b.status) || (b.suggested ?? 0) - (a.suggested ?? 0))

  return (
    <div>
      <div className="mb-3 flex flex-wrap gap-2">
        <Input className="h-9 w-64" placeholder="Search by name or SKU" value={search} onChange={(e) => setSearch(e.target.value)} />
        <Select value={supplier} onValueChange={setSupplier}>
          <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
          <SelectContent align="start">
            <SelectItem value={ALL}>All suppliers</SelectItem>
            {SUPPLIERS.map((name) => (
              <SelectItem key={name} value={name}>{name}</SelectItem>
            ))}
            <SelectItem value={NONE}>No supplier on record</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="rounded-md border bg-card">
        {rows.length === 0 && <div className="px-4 py-6 text-center text-sm">No products match.</div>}
        {rows.map((product) => (
          <div key={product.id} className="flex flex-col gap-2 border-b px-4 py-3 last:border-b-0">
            <div className="flex items-start justify-between gap-6">
              <div className="min-w-0">
                <div className="font-semibold">{product.name}</div>
                <div className="text-xs">
                  {product.sku} · {product.supplier ?? "No supplier on record"} · Lead time {product.leadTimeDays} days
                  {product.moq !== null && ` · Minimum order ${units(product.moq)}`}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-4">
                <Suggestion product={product} />
                <Button size="sm" onClick={() => onAdd(product)}>Add to plan</Button>
              </div>
            </div>
            <div className="rounded-md bg-muted/50 px-3 py-2 text-sm leading-6">
              {product.reasons.map((reason) => (
                <div key={reason}>{reason}</div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
